import ActiveServices from "@/components/measure/ActiveServices";
import { useAuth } from "@/hooks/useAuth";
import { Category, Facility } from "@/types";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CombustionList } from "../companies/tabs/combustion/CombustionList";
import { ElectricityList } from "../companies/tabs/electricity/ElectricityList";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getApiUrl } from "@/utils/api";
import { ArrowLeft } from "lucide-react";

export const FacilitiesDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<
    Category | undefined
  >();

  // Fetch facility details
  const { data: facility, isLoading } = useQuery<Facility>({
    queryKey: ["facility", id],
    queryFn: async () => {
      const response = await fetch(getApiUrl(`facilities/${id}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch facility: ${response.status}`);
      }

      return response.json();
    },
    enabled: !!id,
  });

  // Check user permissions
  useEffect(() => {
    // If facility data is loaded and user data is available
    if (facility && user) {
      // Check if user is admin or has access to this facility through their company
      const isAdmin = user.rol === "admin";
      const isClient = user.rol === "client";
      const hasFacilityAccess =
        facility.id === user.facility?.id ||
        user.company?.id === facility.company;

      // If user doesn't have permission, redirect to facilities list
      if (!(isAdmin || isClient) && !hasFacilityAccess) {
        navigate("/facilities");
      }
    }
  }, [facility, user, navigate]);

  const handleCategorySelect = (category: Category) => {
    console.log("category", category);
    setSelectedCategory(category);
  };

  const handleBackClick = () => {
    navigate("/facilities");
  };

  // Render the appropriate component based on the selected category
  const renderCategoryContent = () => {
    if (!selectedCategory) return null;

    switch (selectedCategory.name) {
      case "combustion":
        return <CombustionList />;
      case "electricity":
        return <ElectricityList />;
      default:
        return <>Choose a category</>;
    }
  };

  return (
    <div>
      <div className="sm:flex sm:items-center mb-8">
        <div className="sm:flex-auto">
          {isLoading ? (
            <h1 className="text-2xl font-semibold">
              Loading facility details...
            </h1>
          ) : facility ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <button
                  className="p-2 rounded-full hover:bg-gray-100 mr-1"
                  onClick={handleBackClick}
                >
                  <ArrowLeft size={20} />
                </button>
                <h1 className="text-2xl font-semibold">{facility.name}</h1>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {facility.address}, {facility.city}, {facility.country}
              </p>
            </>
          ) : (
            <h1 className="text-2xl font-semibold">Facility not found</h1>
          )}
        </div>
      </div>

      <div className="sm:flex sm:items-center">
        <ActiveServices
          facilityId={Number(id)}
          onCategorySelect={handleCategorySelect}
          selectedCategoryId={selectedCategory?.id}
        />
      </div>
      <div className="sm:flex-auto">{renderCategoryContent()}</div>
    </div>
  );
};
